import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ReactNode, useEffect, useState } from 'react';
import AIFullIcon from '@app/assets/icons/AI-full.svg?react';
import ChevronRightBlueIcon from '@app/assets/icons/chevron-right-blue.svg?react';
import { Tooltip } from '@app/components/Tooltip/Tooltip';

interface AIDropdownMinimalProps {
  actions: {
    label: string;
    Icon?: ReactNode;
    onClick?: () => void;
    children?: { label: string; Icon?: ReactNode; onClick?: () => void }[];
  }[];
  showOnClick?: boolean;
  onlyIcon?: boolean;
}

export const AIDropdownMinimal = ({ actions, showOnClick = false, onlyIcon = false }: AIDropdownMinimalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(showOnClick);
  }, [showOnClick]);

  const handleButtonClick = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const handleOptionClick = (onClick?: () => void) => {
    setIsOpen(false);
    onClick?.();
  };

  return (
    <Menu>
      <Tooltip content="Improve with AI" direction="up">
        <MenuButton
          type="button"
          className="flex items-center justify-center gap-2 px-2 py-2 text-sm font-medium text-blue-600 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
          onClick={handleButtonClick}
        >
          <AIFullIcon />
          {onlyIcon ? '' : 'Use AI'}
        </MenuButton>
      </Tooltip>
      <MenuItems
        static={isOpen}
        transition
        anchor="top"
        className="p-2 bg-white shadow-lg min-w-[220px] transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 rounded-lg border border-base-seasalt"
      >
        {actions.map((action, index) => {
          if (action.label === 'Divider') {
            return (
              <MenuItem key={index}>
                <hr className="border-base-antiFlash" />
              </MenuItem>
            );
          }
          
          // If action has children, render a nested menu
          if (action.children && action.children.length > 0) {
            const Icon = action?.Icon ?? null;
            return (
              <Menu key={index} as="div" className="relative">
                <MenuItem>
                  {({ focus }) => (
                    <MenuButton
                      className={`flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-xs w-full ${
                        focus ? 'bg-brand-lightBlue4' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {Icon && Icon}
                        <span>{action.label}</span>
                      </div>
                      <ChevronRightBlueIcon className="ml-auto w-4 h-4" />
                    </MenuButton>
                  )}
                </MenuItem>
                <MenuItems
                  anchor="left"
                  className="p-2 bg-white shadow-lg min-w-[220px] transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 rounded-lg border border-base-seasalt mr-2"
                >
                  {action.children.map((child, childIndex) => {
                    const ChildIcon = child?.Icon ?? null;
                    return (
                      <MenuItem key={childIndex}>
                        <button
                          onClick={() => handleOptionClick(child.onClick)}
                          className="flex items-center gap-2 px-2 py-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full"
                        >
                          {ChildIcon && ChildIcon}
                          {child.label}
                        </button>
                      </MenuItem>
                    );
                  })}
                </MenuItems>
              </Menu>
            );
          }
          
          // Regular menu item without children
          const Icon = action?.Icon ?? null;
          return (
            <MenuItem key={index}>
              <button
                onClick={() => handleOptionClick(action.onClick)}
                className="flex items-center gap-2 px-2 py-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full"
              >
                {Icon && Icon} {action.label}
              </button>
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
};
